import type { StaffRole } from "@lafayette/auth";
import { clientTelemetrySettings } from "@lafayette/domain";

export type AdminEnvironment = "development" | "test" | "staging" | "production";

export type AdminPublicConfig = Readonly<{
  environment: AdminEnvironment;
  version: string;
  gitSha: string;
  buildId: string;
  apiUrl: URL;
  supabaseUrl: URL | null;
  supabasePublishableKey: string | null;
  authRedirectUrl: string;
  sentryDsn: string | null;
  sentrySmokeEnabled: boolean;
  /** CHT-769: the app switch of product analytics (`VITE_LAFAYETTE_ANALYTICS=on`). Default off: no event and no flag call. */
  analyticsEnabled: boolean;
  /**
   * CHT-770: the telemetry debug setting (`VITE_LAFAYETTE_TELEMETRY_DEBUG`: off, verbose or raw). Default `off`. Production accepts only `off`,
   * and staging refuses `raw`: the build stops with a message that names the rule and never echoes the value. Left out in a test config: off.
   */
  telemetryDebug?: string | undefined;
  /**
   * CHT-1628: `VITE_LAFAYETTE_REQUIRE_STAFF_AAL2=true` makes the sign-in ask for the MFA code outside production too, to match an API that
   * runs with `LAFAYETTE_REQUIRE_STAFF_AAL2=true`. Production always asks. Left out in a test config: off.
   */
  requireStaffAal2?: boolean;
  developmentAuthPlaceholderEnabled: boolean;
  developmentStaffRole: StaffRole;
  /** The seed staff subject the development placeholder signs in as. `null` when unset or malformed. */
  developmentStaffSubject: string | null;
}>;

const environments = new Set<AdminEnvironment>(["development", "test", "staging", "production"]);
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const roles = new Set<StaffRole>(["concierge", "supervisor", "catalog_editor", "administrator"]);

export function getAdminPublicConfig(): AdminPublicConfig {
  const environmentValue = import.meta.env.VITE_LAFAYETTE_ENV ?? "development";
  if (!environments.has(environmentValue as AdminEnvironment)) {
    throw new Error("Invalid Lafayette admin environment configuration.");
  }

  const apiUrl = new URL(import.meta.env.VITE_API_URL ?? "http://localhost:8787");
  if (apiUrl.protocol !== "http:" && apiUrl.protocol !== "https:") {
    throw new Error("Admin API URL must use HTTP or HTTPS.");
  }

  const supabaseUrlValue = import.meta.env.VITE_SUPABASE_URL;
  const supabaseUrl = supabaseUrlValue ? new URL(supabaseUrlValue) : null;
  if (supabaseUrl && supabaseUrl.protocol !== "https:") {
    throw new Error("Admin Supabase URL must use HTTPS.");
  }
  const supabasePublishableKey =
    import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? import.meta.env.VITE_SUPABASE_ANON_KEY ?? null;
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN ?? null;

  // CHT-770: a debug mode that the environment refuses stops the start, as it does in the API.
  const telemetryDebug = import.meta.env.VITE_LAFAYETTE_TELEMETRY_DEBUG;
  clientTelemetrySettings(telemetryDebug, environmentValue as AdminEnvironment);

  const developmentAuthPlaceholderEnabled =
    environmentValue === "development" &&
    import.meta.env.VITE_ENABLE_DEVELOPMENT_AUTH_PLACEHOLDER === "true";

  const roleValue = import.meta.env.VITE_DEVELOPMENT_STAFF_ROLE ?? "concierge";
  const developmentStaffRole = roles.has(roleValue as StaffRole)
    ? (roleValue as StaffRole)
    : "concierge";

  const subjectValue = import.meta.env.VITE_DEVELOPMENT_STAFF_SUBJECT;
  const developmentStaffSubject =
    typeof subjectValue === "string" && uuidPattern.test(subjectValue) ? subjectValue : null;

  return {
    environment: environmentValue as AdminEnvironment,
    version: import.meta.env.VITE_VERSION ?? "local",
    gitSha: import.meta.env.VITE_GIT_SHA ?? "local",
    buildId: import.meta.env.VITE_BUILD_ID ?? "local",
    apiUrl,
    supabaseUrl,
    supabasePublishableKey,
    authRedirectUrl:
      import.meta.env.VITE_AUTH_REDIRECT_URL ?? `${window.location.origin}/auth/callback`,
    sentryDsn,
    sentrySmokeEnabled:
      environmentValue === "staging" && import.meta.env.VITE_SENTRY_SMOKE_TEST === "true",
    analyticsEnabled: import.meta.env.VITE_LAFAYETTE_ANALYTICS === "on",
    telemetryDebug,
    requireStaffAal2: import.meta.env.VITE_LAFAYETTE_REQUIRE_STAFF_AAL2 === "true",
    developmentAuthPlaceholderEnabled,
    developmentStaffRole,
    developmentStaffSubject,
  };
}
